import { getCollectionDesignById, getCollectionDesignContent } from "../content/collectionDesigns";
import { getCollectionNaming } from "../content/collectionNaming";
import { getSeoLandingContent, getSeoLandingSlugs } from "../content/seoLandings";
import { buildOptimizedAssetUrl, CELOSIAS_EDITORIAL_COVER_URL, normalizeDesignMedia, pickStableImage } from "./utils";

const BASE_URL = "https://www.aristaconcreto.com";
const CATALOGS_CANONICAL_URL = "https://aristaconcreto.com/catalogos";
const CATALOGS_SOCIAL_IMAGE_URL = "https://aristaconcreto.com/social/catalogos-social.png";

const BRAND_NAME = "Arista + Vértice";
const SOCIAL_LINKS = [
  "https://instagram.com/aristamasvertice",
  "https://www.facebook.com/aristamasvertice",
  "https://www.linkedin.com/company/aristamasvertice",
  "https://www.pinterest.com/aristamasvertice",
  "https://www.threads.net/@aristamasvertice",
];

const HTML_LANG = {
  "es-MX": "es-MX",
  "es-ES": "es-ES",
  "en-US": "en-US",
};

const OG_LOCALE = {
  "es-MX": "es_MX",
  "es-ES": "es_ES",
  "en-US": "en_US",
};

const ROUTE_URL_OVERRIDES = {
  "/catalogos": CATALOGS_CANONICAL_URL,
};

const KEYWORDS_BY_LOCALE = {
  "es-MX": [
    "Arista + Vértice",
    "losetas de concreto",
    "losetas 3D",
    "paneles de concreto",
    "celosías de concreto",
    "acabados de concreto",
    "acabados arquitectónicos",
    "recubrimientos para muros",
    "revestimientos de concreto",
    "diseño interior",
    "interiorismo",
    "arquitectura",
    "materiales para arquitectos",
    "materiales para interioristas",
    "fachadas arquitectónicas",
    "muros decorativos",
  ],
  "es-ES": [
    "Arista + Vértice",
    "losetas de hormigón",
    "losetas 3D",
    "paneles de hormigón",
    "celosías de hormigón",
    "acabados de hormigón",
    "acabados arquitectónicos",
    "revestimientos para muros",
    "revestimientos de hormigón",
    "diseño interior",
    "interiorismo",
    "arquitectura",
    "materiales para arquitectos",
    "materiales para interioristas",
    "fachadas arquitectónicas",
    "muros decorativos",
  ],
  "en-US": [
    "Arista + Vértice",
    "wall tiles",
    "concrete wall tiles",
    "3D concrete tiles",
    "architectural concrete finishes",
    "concrete screens",
    "decorative concrete panels",
    "concrete wall cladding",
    "concrete facade materials",
    "materials for architects",
    "materials for interior designers",
  ],
};

const STATIC_PAGE_COPY = {
  "es-MX": {
    home: {
      title: "Arista + Vértice | Losetas, paneles y celosías de concreto",
      description:
        "Acabados neoartesanales de concreto para arquitectura e interiorismo premium: losetas 3D, paneles, celosías, revestimientos para muros y fachadas arquitectónicas.",
    },
    collections: {
      title: "Colecciones de concreto arquitectónico | Arista + Vértice",
      description:
        "Explora colecciones de losetas de concreto, losetas 3D, paneles de concreto, celosías y soluciones para muros decorativos, fachadas e interiorismo.",
    },
    catalogs: {
      title: "Catálogo 2026 | Arista + Vértice",
      description:
        "Descubre nuestras colecciones de acabados neoartesanales de concreto.",
    },
    textures: {
      title: "Texturas y acabados de concreto | Arista + Vértice",
      description:
        "Descubre texturas y acabados arquitectónicos de concreto para muros, fachadas y espacios de interiorismo con un lenguaje material sofisticado.",
    },
    colors: {
      title: "Colores para concreto arquitectónico | Arista + Vértice",
      description:
        "Explora la paleta cromática de Arista + Vértice para personalizar losetas, paneles y celosías de concreto en proyectos residenciales y comerciales.",
    },
    manuals: {
      title: "Manuales de instalación y descarga | Arista + Vértice",
      description:
        "Consulta manuales de instalación, descarga, limpieza y mantenimiento para acabados de concreto en muros, paneles, pisos y celosías.",
    },
    finishes: {
      title: "Acabados arquitectónicos premium | Arista + Vértice",
      description:
        "Explora acabados arquitectónicos premium de concreto para interiorismo, muros decorativos y fachadas con una lectura sobria y contemporánea.",
    },
    configurator: {
      title: "Configurador de acabados de concreto | Arista + Vértice",
      description:
        "Configura materiales, texturas, colores y familias de producto para definir acabados de concreto alineados con tu proyecto arquitectónico.",
    },
    allies: {
      title: "Aliados: arquitectos e interioristas | Arista + Vértice",
      description:
        "Arista + Vértice trabaja con arquitectos, interioristas y desarrolladores que buscan materiales con carácter, personalización y soporte comercial.",
    },
    contact: {
      title: "Contacto y showroom CDMX | Arista + Vértice",
      description:
        "Solicita información por WhatsApp, agenda visita al showroom en Ciudad de México y conoce soluciones de concreto arquitectónico para tu proyecto.",
    },
  },
  "es-ES": {
    home: {
      title: "Arista + Vértice | Losetas, paneles y celosías de hormigón",
      description:
        "Acabados neoartesanales de hormigón para arquitectura e interiorismo premium: losetas 3D, paneles, celosías, revestimientos para muros y fachadas arquitectónicas.",
    },
    collections: {
      title: "Colecciones de hormigón arquitectónico | Arista + Vértice",
      description:
        "Explora colecciones de losetas de hormigón, losetas 3D, paneles arquitectónicos, celosías y revestimientos para interiorismo, fachadas y muros decorativos.",
    },
    catalogs: {
      title: "Catálogo 2026 | Arista + Vértice",
      description:
        "Descubre nuestras colecciones de acabados neoartesanales de concreto.",
    },
    textures: {
      title: "Texturas y acabados de hormigón | Arista + Vértice",
      description:
        "Descubre texturas y acabados arquitectónicos de hormigón para muros, fachadas y espacios de interiorismo con una expresión material sofisticada.",
    },
    colors: {
      title: "Colores para hormigón arquitectónico | Arista + Vértice",
      description:
        "Explora la paleta cromática de Arista + Vértice para personalizar losetas, paneles y celosías de hormigón en proyectos residenciales y contract.",
    },
    manuals: {
      title: "Manuales de instalación y descarga | Arista + Vértice",
      description:
        "Consulta manuales de instalación, descarga, limpieza y mantenimiento para acabados de hormigón en muros, paneles, suelos y celosías.",
    },
    finishes: {
      title: "Acabados arquitectónicos premium | Arista + Vértice",
      description:
        "Explora acabados arquitectónicos premium de hormigón para interiorismo, muros decorativos y fachadas con una lectura sobria y contemporánea.",
    },
    configurator: {
      title: "Configurador de acabados de hormigón | Arista + Vértice",
      description:
        "Configura materiales, texturas, colores y familias de producto para definir acabados de hormigón alineados con tu proyecto arquitectónico.",
    },
    allies: {
      title: "Aliados: arquitectos e interioristas | Arista + Vértice",
      description:
        "Arista + Vértice trabaja con arquitectos, interioristas y promotores que buscan materiales con carácter, personalización y acompañamiento comercial.",
    },
    contact: {
      title: "Contacto y showroom | Arista + Vértice",
      description:
        "Solicita información por WhatsApp, agenda visita al showroom y descubre soluciones de hormigón arquitectónico para proyectos en España y mercados internacionales.",
    },
  },
  "en-US": {
    home: {
      title: "Arista + Vértice | Architectural concrete wall finishes",
      description:
        "Premium architectural concrete finishes for walls, facades and interior design: concrete wall tiles, 3D concrete tiles, decorative panels and concrete screens.",
    },
    collections: {
      title: "Concrete wall tiles, screens and panels | Arista + Vértice",
      description:
        "Explore concrete wall tiles, 3D concrete tiles, decorative concrete panels and concrete screens for interior designers, architects and facade projects.",
    },
    catalogs: {
      title: "2026 Catalog | Arista + Vértice",
      description:
        "Discover our collections of neo-artisanal concrete finishes.",
    },
    textures: {
      title: "Concrete textures and finishes | Arista + Vértice",
      description:
        "Discover architectural concrete textures and finish options for walls, facades and interior design applications with a refined editorial language.",
    },
    colors: {
      title: "Color palette for architectural concrete | Arista + Vértice",
      description:
        "Explore the material color palette behind Arista + Vértice collections for concrete wall tiles, decorative panels and custom specifications.",
    },
    manuals: {
      title: "Installation manuals | Arista + Vértice",
      description:
        "Access installation, unloading, cleaning and care manuals for concrete wall tiles, panels, floors and breeze block systems.",
    },
    finishes: {
      title: "Premium architectural finishes | Arista + Vértice",
      description:
        "Explore premium architectural concrete finishes for decorative walls, facade systems and interior design projects with strong material expression.",
    },
    configurator: {
      title: "Concrete finish configurator | Arista + Vértice",
      description:
        "Configure materials, textures, colors and product families to define architectural concrete finishes aligned with your project goals.",
    },
    allies: {
      title: "For architects and interior designers | Arista + Vértice",
      description:
        "Arista + Vértice supports architects, interior designers and developers seeking sculptural concrete finishes with customization and commercial guidance.",
    },
    contact: {
      title: "Contact and showroom | Arista + Vértice",
      description:
        "Request information, connect on WhatsApp and explore Arista + Vértice solutions for premium architectural concrete projects.",
    },
  },
};

const COLLECTION_COPY = {
  "es-MX": {
    muro: {
      title: "Losetas 3D y revestimientos de concreto | Arista + Vértice",
      description:
        `La colección ${getCollectionNaming("es-MX", "muro").title} reúne ${getCollectionNaming("es-MX", "muro").designPluralLabel}, recubrimientos para muros y revestimientos de concreto para interiorismo, muros decorativos y fachadas arquitectónicas.`,
      designLabel: getCollectionNaming("es-MX", "muro").designLabel,
    },
    celosias: {
      title: "Celosías de concreto para fachadas | Arista + Vértice",
      description:
        `Explora ${getCollectionNaming("es-MX", "celosias").designPluralLabel} y pantallas arquitectónicas para fachadas, privacidad, ventilación y composiciones con luz y sombra.`,
      designLabel: getCollectionNaming("es-MX", "celosias").designLabel,
    },
    paneles: {
      title: "Paneles de concreto arquitectónico | Arista + Vértice",
      description:
        `Explora ${getCollectionNaming("es-MX", "paneles").designPluralLabel} para muros, revestimientos de gran formato y superficies con presencia escultórica para proyectos premium.`,
      designLabel: getCollectionNaming("es-MX", "paneles").designLabel,
    },
    pisos: {
      title: "Losetas de concreto para pisos | Arista + Vértice",
      description:
        `Descubre ${getCollectionNaming("es-MX", "pisos").designPluralLabel} y superficies de alto tránsito con lectura monolítica, detalle material y especificación arquitectónica.`,
      designLabel: getCollectionNaming("es-MX", "pisos").designLabel,
    },
  },
  "es-ES": {
    muro: {
      title: "Losetas 3D y revestimientos de hormigón | Arista + Vértice",
      description:
        `La colección ${getCollectionNaming("es-ES", "muro").title} reúne ${getCollectionNaming("es-ES", "muro").designPluralLabel}, revestimientos para muros y soluciones de hormigón arquitectónico para interiorismo y fachadas.`,
      designLabel: getCollectionNaming("es-ES", "muro").designLabel,
    },
    celosias: {
      title: "Celosías de hormigón para fachadas | Arista + Vértice",
      description:
        `Explora ${getCollectionNaming("es-ES", "celosias").designPluralLabel} y pantallas arquitectónicas para fachadas, privacidad, ventilación y composiciones de luz y sombra.`,
      designLabel: getCollectionNaming("es-ES", "celosias").designLabel,
    },
    paneles: {
      title: "Paneles de hormigón arquitectónico | Arista + Vértice",
      description:
        `Explora ${getCollectionNaming("es-ES", "paneles").designPluralLabel} para muros, fachadas y superficies de gran formato con presencia escultórica y lectura editorial.`,
      designLabel: getCollectionNaming("es-ES", "paneles").designLabel,
    },
    pisos: {
      title: "Losetas de hormigón para suelos | Arista + Vértice",
      description:
        `Descubre ${getCollectionNaming("es-ES", "pisos").designPluralLabel} y superficies de alto tránsito con lectura monolítica, detalle material y prescripción arquitectónica.`,
      designLabel: getCollectionNaming("es-ES", "pisos").designLabel,
    },
  },
  "en-US": {
    muro: {
      title: "Concrete wall tiles and 3D wall finishes | Arista + Vértice",
      description:
        `The ${getCollectionNaming("en-US", "muro").title} collection brings together ${getCollectionNaming("en-US", "muro").designPluralLabel}, 3D wall finishes and decorative concrete surfaces for premium interiors and architectural walls.`,
      designLabel: getCollectionNaming("en-US", "muro").designLabel,
    },
    celosias: {
      title: `${getCollectionNaming("en-US", "celosias").title} for facades | Arista + Vértice`,
      description:
        `Explore ${getCollectionNaming("en-US", "celosias").designPluralLabel} and breeze block systems for facades, privacy, airflow and shadow-driven architectural compositions.`,
      designLabel: getCollectionNaming("en-US", "celosias").designLabel,
    },
    paneles: {
      title: `${getCollectionNaming("en-US", "paneles").title} | Arista + Vértice`,
      description:
        `${getCollectionNaming("en-US", "paneles").title} for walls, cladding systems and large-format surfaces with sculptural depth and material precision.`,
      designLabel: getCollectionNaming("en-US", "paneles").designLabel,
    },
    pisos: {
      title: `${getCollectionNaming("en-US", "pisos").title} | Arista + Vértice`,
      description:
        `${getCollectionNaming("en-US", "pisos").title} for premium interiors and exterior circulation areas with refined material presence and architectural performance.`,
      designLabel: getCollectionNaming("en-US", "pisos").designLabel,
    },
  },
};

const PATH_LABELS = {
  "es-MX": {
    "/": "Inicio",
    "/colecciones": "Colecciones",
    "/catalogos": "Catálogos",
    "/texturas": "Texturas",
    "/colores": "Colores",
    "/manuales": "Manuales",
    "/acabados": "Acabados",
    "/configurador": "Configurador",
    "/aliados": "Aliados",
    "/contacto": "Contacto",
  },
  "es-ES": {
    "/": "Inicio",
    "/colecciones": "Colecciones",
    "/catalogos": "Catálogos",
    "/texturas": "Texturas",
    "/colores": "Colores",
    "/manuales": "Manuales",
    "/acabados": "Acabados",
    "/configurador": "Configurador",
    "/aliados": "Aliados",
    "/contacto": "Contacto",
  },
  "en-US": {
    "/": "Home",
    "/colecciones": "Collections",
    "/catalogos": "Catalogs",
    "/texturas": "Textures",
    "/colores": "Colors",
    "/manuales": "Manuals",
    "/acabados": "Finishes",
    "/configurador": "Configurator",
    "/aliados": "Allies",
    "/contacto": "Contact",
    "/losetas-de-concreto": getCollectionNaming("en-US", "muro").title,
    "/celosias": getCollectionNaming("en-US", "celosias").title,
    "/celosias-de-concreto": getCollectionNaming("en-US", "celosias").title,
    "/paneles-de-concreto": getCollectionNaming("en-US", "paneles").title,
    "/wall-tiles": getCollectionNaming("en-US", "muro").title,
    "/concrete-wall-tiles": getCollectionNaming("en-US", "muro").title,
    "/concrete-screens": getCollectionNaming("en-US", "celosias").title,
  },
};

const SEO_LANDING_ALIASES = {
  "/celosias": "celosias-de-concreto",
  "/concrete-wall-tiles": "wall-tiles",
};

const toSeoImageUrl = (src) => {
  if (!src) {
    return null;
  }

  const optimized = buildOptimizedAssetUrl(src, { width: 1440, quality: 76 });

  if (!optimized) {
    return null;
  }

  if (optimized.startsWith("http") || optimized.startsWith("data:")) {
    return optimized;
  }

  return `${BASE_URL}${optimized.startsWith("/") ? optimized : `/${optimized}`}`;
};

const resolveCollectionSeoImage = (collectionId, collection) => {
  if (collectionId === "celosias") {
    return toSeoImageUrl(collection?.seoImage || collection?.ogImage || CELOSIAS_EDITORIAL_COVER_URL || collection?.image);
  }

  return toSeoImageUrl(collection?.seoImage || collection?.ogImage || collection?.image);
};

const resolveSeoLandingImage = (landing, heroCollection) => {
  if (landing?.heroCollectionId === "celosias") {
    return toSeoImageUrl(CELOSIAS_EDITORIAL_COVER_URL);
  }

  return toSeoImageUrl(heroCollection?.image);
};

const buildLocalizedUrl = (pathname) => {
  const targetPath = pathname || "/";
  if (ROUTE_URL_OVERRIDES[targetPath]) {
    return ROUTE_URL_OVERRIDES[targetPath];
  }
  return `${BASE_URL}${targetPath}`;
};

const buildKeywordString = (locale, extraKeywords = []) => {
  return Array.from(new Set([...(KEYWORDS_BY_LOCALE[locale] || []), ...extraKeywords])).join(", ");
};

const humanizeDesignId = (value = "") =>
  value
    .split("-")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

const buildOrganizationSchema = (data, locale) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND_NAME,
  url: buildLocalizedUrl("/"),
  logo: toSeoImageUrl("https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/336sf0vp_AristaLogos-01.png"),
  sameAs: SOCIAL_LINKS,
  email: data.contact?.main_email,
  telephone: data.contact?.office_phone,
  contactPoint: [
    data.contact?.sales_whatsapp
      ? {
          "@type": "ContactPoint",
          contactType: locale === "en-US" ? "sales" : "ventas",
          telephone: data.contact.sales_whatsapp,
          areaServed: ["MX", "ES", "US"],
          availableLanguage: ["Spanish", "English"],
        }
      : null,
  ].filter(Boolean),
});

const buildLocalBusinessSchema = (data, locale) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: `${BRAND_NAME} Showroom CDMX`,
  url: buildLocalizedUrl("/contacto"),
  image: toSeoImageUrl(data.showroom?.image),
  telephone: data.contact?.office_phone,
  email: data.contact?.main_email,
  address: {
    "@type": "PostalAddress",
    streetAddress: data.showroom?.address,
    addressLocality: "Ciudad de México",
    addressRegion: "CDMX",
    addressCountry: "MX",
  },
  areaServed: ["MX", "ES", "US"],
  sameAs: SOCIAL_LINKS,
});

const buildWebsiteSchema = (locale) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: BRAND_NAME,
  url: buildLocalizedUrl("/"),
  inLanguage: locale,
});

const getCollectionSchemaItems = (locale, collectionId) => {
  const content = getCollectionDesignContent(locale, collectionId);
  return content?.designs?.slice(0, 18) || [];
};

const buildCollectionPageSchema = ({ locale, pathname, collection, title, description, items }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: title,
  description,
  url: buildLocalizedUrl(pathname),
  about: collection?.title || title,
  mainEntity: {
    "@type": "ItemList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: buildLocalizedUrl(`/${collection.id}/${item.id}`),
      name: item.title,
    })),
  },
});

const buildProductSchema = ({ locale, pathname, design, collectionTitle, categoryLabel, description }) => {
  const normalized = normalizeDesignMedia(design);
  const images = [
    pickStableImage(normalized.thumbnail, normalized.detailImage, normalized.contextImage, normalized.measurementImage),
    normalized.detailImage,
    normalized.contextImage,
    normalized.measurementImage,
  ]
    .filter(Boolean)
    .map((src) => toSeoImageUrl(src));

  const dimensions = normalized.specs?.dimensions || normalized.variants?.[0]?.dimensions;
  const additionalProperty = [
    dimensions ? { "@type": "PropertyValue", name: locale === "en-US" ? "Dimensions" : "Medidas", value: dimensions } : null,
    normalized.specs?.piecesPerM2 ? { "@type": "PropertyValue", name: locale === "en-US" ? "Pieces per square meter" : "Piezas por m²", value: normalized.specs.piecesPerM2 } : null,
    normalized.specs?.weightPerPiece ? { "@type": "PropertyValue", name: locale === "en-US" ? "Weight per piece" : "Peso por pieza", value: normalized.specs.weightPerPiece } : null,
  ].filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: design.title,
    brand: { "@type": "Brand", name: BRAND_NAME },
    category: collectionTitle,
    material: locale === "es-ES" ? "Hormigón arquitectónico" : locale === "en-US" ? "Architectural concrete" : "Concreto arquitectónico",
    description,
    image: images,
    sku: design.id,
    url: buildLocalizedUrl(pathname),
    additionalProperty,
  };
};

const buildBreadcrumbs = ({ locale, pathname, collection, design }) => {
  const labels = PATH_LABELS[locale] || PATH_LABELS["es-MX"];
  const crumbs = [{ name: labels["/"] || BRAND_NAME, url: buildLocalizedUrl("/") }];

  if (pathname === "/") {
    return crumbs;
  }

  const segments = pathname.split("/").filter(Boolean);
  let runningPath = "";

  segments.forEach((segment, index) => {
    runningPath += `/${segment}`;
    if (index === 0 && labels[runningPath]) {
      crumbs.push({ name: labels[runningPath], url: buildLocalizedUrl(runningPath) });
      return;
    }

    if (index === 1 && collection && pathname.startsWith("/colecciones/")) {
      crumbs.push({ name: collection.title, url: buildLocalizedUrl(runningPath) });
      return;
    }

    if (index === 0 && design && collection) {
      crumbs.push({ name: PATH_LABELS[locale]["/colecciones"] || PATH_LABELS["es-MX"]["/colecciones"], url: buildLocalizedUrl("/colecciones") });
      crumbs.push({ name: collection.title, url: buildLocalizedUrl(`/colecciones/${collection.id}`) });
      return;
    }

    if (index === 1 && design) {
      crumbs.push({ name: design.title, url: buildLocalizedUrl(runningPath) });
    }
  });

  return crumbs;
};

export const getSeoPayload = ({ pathname, locale, data }) => {
  const cleanPath = pathname || "/";
  const normalizedPath = cleanPath.replace(/\/+$/, "") || "/";
  const routePath = normalizedPath;
  const resolvedLandingSlug = SEO_LANDING_ALIASES[normalizedPath] || normalizedPath.replace(/^\//, "");
  const copy = STATIC_PAGE_COPY[locale] || STATIC_PAGE_COPY["es-MX"];
  const keywords = [...(KEYWORDS_BY_LOCALE[locale] || [])];
  const schemas = [buildOrganizationSchema(data, locale)];
  const alternateLocales = [];

  if (getSeoLandingSlugs().includes(resolvedLandingSlug)) {
    const landing = getSeoLandingContent(resolvedLandingSlug, locale);
    const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath });
    const heroCollection = data.collections.find((item) => item.id === landing?.heroCollectionId);
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
    schemas.push({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: landing.seoTitle,
      description: landing.seoDescription,
      url: buildLocalizedUrl(routePath),
      about: landing.title,
      mentions: landing.relatedCollectionIds.map((id) => ({
        "@type": "Thing",
        name: data.collections.find((item) => item.id === id)?.title || id,
      })),
    });

    return {
      title: landing.seoTitle,
      description: landing.seoDescription,
      keywords: buildKeywordString(locale, landing.keywords || []),
      canonical: buildLocalizedUrl(routePath),
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image: resolveSeoLandingImage(landing, heroCollection),
      schemas,
      alternateLocales,
      type: "website",
    };
  }

  if (routePath === "/") {
    const image = toSeoImageUrl(data.showroom?.image || data.collections?.[0]?.image);
    schemas.push(buildWebsiteSchema(locale), buildLocalBusinessSchema(data, locale));

    return {
      title: copy.home.title,
      description: copy.home.description,
      keywords: buildKeywordString(locale),
      canonical: buildLocalizedUrl(routePath),
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image,
      schemas,
      alternateLocales,
      type: "website",
    };
  }

  if (routePath === "/colecciones") {
    const image = toSeoImageUrl(CELOSIAS_EDITORIAL_COVER_URL);
    const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath });
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
    schemas.push(
      buildCollectionPageSchema({
        locale,
        pathname: routePath,
        collection: { id: "colecciones", title: PATH_LABELS[locale]["/colecciones"] },
        title: copy.collections.title,
        description: copy.collections.description,
        items: data.collections.map((item) => ({ id: item.id, title: item.title })),
      }),
    );

    return {
      title: copy.collections.title,
      description: copy.collections.description,
      keywords: buildKeywordString(locale, ["colecciones de concreto", "colecciones arquitectónicas"]),
      canonical: buildLocalizedUrl(routePath),
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image,
      schemas,
      alternateLocales,
      type: "website",
    };
  }

  if (routePath === "/catalogos") {
    const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath });
    schemas.push(buildBreadcrumbSchema(breadcrumbs));

    return {
      title: copy.catalogs.title,
      description: copy.catalogs.description,
      keywords: buildKeywordString(locale, [locale === "en-US" ? "2026 catalog" : "catálogo 2026", locale === "en-US" ? "neo-artisanal concrete finishes" : "acabados neoartesanales de concreto"]),
      canonical: CATALOGS_CANONICAL_URL,
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image: CATALOGS_SOCIAL_IMAGE_URL,
      schemas,
      alternateLocales,
      type: "website",
    };
  }

  if (routePath.startsWith("/colecciones/")) {
    const collectionId = routePath.split("/")[2];
    const collection = data.collections.find((item) => item.id === collectionId);
    const collectionCopy = COLLECTION_COPY[locale]?.[collectionId];
    const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath, collection });
    const items = getCollectionSchemaItems(locale, collectionId);
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
    if (collection && collectionCopy) {
      schemas.push(
        buildCollectionPageSchema({
          locale,
          pathname: routePath,
          collection,
          title: collectionCopy.title,
          description: collectionCopy.description,
          items,
        }),
      );
    }

    return {
      title: collection?.metaTitle || collectionCopy?.title || `${collection?.title || BRAND_NAME} | ${BRAND_NAME}`,
      description: collection?.metaDescription || collectionCopy?.description || collection?.description || copy.collections.description,
      keywords: buildKeywordString(locale, [collection?.title, collectionCopy?.designLabel].filter(Boolean)),
      canonical: buildLocalizedUrl(routePath),
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image: resolveCollectionSeoImage(collectionId, collection),
      schemas,
      alternateLocales,
      type: "website",
    };
  }

  const designRouteMatch = routePath.match(/^\/(muro|celosias|paneles|pisos)\/([^/?#]+)/i);
  if (designRouteMatch) {
    const collectionId = designRouteMatch[1];
    const designId = designRouteMatch[2];
    const collection = data.collections.find((item) => item.id === collectionId);
    const collectionCopy = COLLECTION_COPY[locale]?.[collectionId];
    const detail = getCollectionDesignById(locale, collectionId, designId);
    const design = detail?.design;
    const designLabel = design?.title || humanizeDesignId(designId);
    const localizedDescription = typeof design?.description === "string" ? design.description : collectionCopy?.description;
    const description = localizedDescription
      ? `${localizedDescription} ${locale === "en-US" ? "Request information on WhatsApp or explore the catalog for architects and interior designers." : "Solicita información por WhatsApp o explora el catálogo para arquitectos e interioristas."}`
      : collectionCopy?.description || copy.home.description;
    const title = collectionCopy
      ? `${designLabel} | ${collectionCopy.designLabel} | ${BRAND_NAME}`
      : `${designLabel || BRAND_NAME} | ${BRAND_NAME}`;
    const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath, design: { title: designLabel }, collection });
    schemas.push(buildBreadcrumbSchema(breadcrumbs));
    if (design && collectionCopy) {
      schemas.push(
        buildProductSchema({
          locale,
          pathname: routePath,
          design,
          collectionTitle: collection?.title || collectionId,
          categoryLabel: collectionCopy.designLabel,
          description,
        }),
      );
    }

    return {
      title,
      description,
      keywords: buildKeywordString(locale, [designLabel, collectionCopy?.designLabel, collection?.title].filter(Boolean)),
      canonical: buildLocalizedUrl(routePath),
      htmlLang: HTML_LANG[locale],
      ogLocale: OG_LOCALE[locale],
      image: toSeoImageUrl(pickStableImage(design?.thumbnail, design?.detailImage, design?.contextImage, collection?.image)),
      schemas,
      alternateLocales,
      type: "product",
    };
  }

  const staticPathMap = {
    "/texturas": copy.textures,
    "/colores": copy.colors,
    "/manuales": copy.manuals,
    "/acabados": copy.finishes,
    "/configurador": copy.configurator,
    "/aliados": copy.allies,
    "/contacto": copy.contact,
  };
  const staticMeta = staticPathMap[routePath] || copy.home;
  const breadcrumbs = buildBreadcrumbs({ locale, pathname: routePath });
  schemas.push(buildBreadcrumbSchema(breadcrumbs));

  if (routePath === "/contacto") {
    schemas.push(buildLocalBusinessSchema(data, locale));
  }

  return {
    title: staticMeta.title,
    description: staticMeta.description,
    keywords: buildKeywordString(locale),
    canonical: buildLocalizedUrl(routePath),
    htmlLang: HTML_LANG[locale],
    ogLocale: OG_LOCALE[locale],
    image: toSeoImageUrl(data.showroom?.image || data.collections?.[0]?.image),
    schemas,
    alternateLocales,
    type: routePath === "/contacto" ? "website" : "article",
  };
};
